import React, { useState, useEffect, useMemo } from 'react';
import { isEmpty, isEqual, get } from 'lodash';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { logInfo, logError } from 'commons/logger';
import JBImage from 'components/essentials/JBImage';
import Quantity from 'components/consumer/Quantity';
import { getFinalPrice } from 'data-hooks/useModularPDPCart/utils';
import IconPlus from 'components/icons/iconPlus';
import InfoTooltip from 'components/consumer/InfoTooltip';
import PDPMobileGalleryZoomIcon from 'components/consumer/PDP/PDPMobileGalleryZoomIcon';
import { colorBrand, colorGrayLight4 } from 'styles/layout/colors';
import useScreen from 'hooks/useScreen';
import { MISSING_OPTION_MSG, getImagesFromSelectedOptions, getCurrentProductQuantity, getItemDimensions } from './utils';
const ModularItem = ({
  selectedOptions,
  selectedOptionsExtended,
  modularState = {},
  product = null,
  options,
  updateModularOrder,
  updateGalleryProduct,
  isSelectionInProductOptions,
  onThumbnailClick,
  selectedOptionsVariantSku = false,
  setHideMarxent,
  unavailableFabric,
  marxentInitialised,
  setMarxentInitialised,
  slug,
  setMarxent,
  marxent,
  productsIn3dLoader,
  scrollToGallery
}) => {
  const {
    modularBundleOrderStatus,
    currentModularGalleryProduct,
    collectionTitle
  } = modularState;
  const {
    orientation = {}
  } = product;
  const [productImages, setProductImages] = useState(getImagesFromSelectedOptions({
    selectedOptionsExtended,
    product
  }));
  const [lastImage, setLastImage] = useState('');
  const [imgLoadFailed, setImageLoadFailed] = useState(false);
  const {
    isMobileOrTablet,
    isMobile
  } = useScreen();
  const isDisabled = useMemo(() => product?.type?.name?.toLowerCase().endsWith('ottoman') && !marxentInitialised || unavailableFabric, [marxentInitialised, product?.type?.name, unavailableFabric]);
  const min_quantity = product && product.attributes ? get(product.attributes, {
    code: 'min_qty'
  }) : 0;
  let updatedProductName = product.name.replace(`${collectionTitle} `, '');
  if (!isEmpty(orientation)) {
    const {
      currentSelection = {}
    } = orientation;
    updatedProductName = !isEmpty(currentSelection) ? `${currentSelection.value} ${updatedProductName}` : updatedProductName;
  }
  const currentProductQuantity = getCurrentProductQuantity({
    productId: product.id,
    orientation,
    modularBundleOrderStatus
  });
  const isCurrentModularSelected = currentModularGalleryProduct && parseInt(currentModularGalleryProduct.id, 10) === parseInt(product.id, 10) && (isEmpty(orientation) || isEqual(currentModularGalleryProduct.orientation, orientation));
  const {
    price,
    discount_percent,
    original_price
  } = getFinalPrice({
    bundleMeta: product.meta,
    selectedOptions,
    options
  });
  const {
    sizeWidth = 0,
    sizeHeight = 0,
    sizeDepth = 0
  } = getItemDimensions({
    attributes: product.attributes
  });
  const resetVariantGallery = () => {
    // Reset variant gallery to modular product if not already set
    if (!isCurrentModularSelected) {
      updateGalleryProduct(product);
    }
  };
  const removeItemToOrder = () => {
    if (currentProductQuantity === 0) return;

    // This condition is to handle Marxent's odd requirement:
    // An ottoman cannot be left on its own in the 3d builder.
    // Example: If the builder contains "Armless Chair" and "Ottoman",
    // the Ottoman should be removed first. Removing the Armless Chair first
    // freezes the Marxent's 3d builder.
    if (marxent && productsIn3dLoader.current.length === 2 && productsIn3dLoader.current.map(x => x.productType).includes('Ottoman') && product?.type?.name && !product.type.name.toLowerCase().includes('ottoman')) {
      return;
    }
    let equivalentMarxId;
    if (product?.orientation && Object.keys(product.orientation).length) {
      equivalentMarxId = `${product.sku}${product.orientation.currentSelection.sku}`;
    } else {
      equivalentMarxId = product.sku;
    }
    logInfo('Selected SKU: ', equivalentMarxId);
    if (marxent && product?.type?.name && !product?.type?.name?.toLowerCase().endsWith('slipcover')) {
      const eventsManager = marxent.getEventManager();
      const marxProducts = eventsManager.getProductsData()?.activeProductsData;
      const indexToDelete = marxProducts.findIndex(marxProduct => marxProduct.sku.split('|')[0] === equivalentMarxId);
      eventsManager.deleteProduct({
        index: indexToDelete
      });
      resetVariantGallery();
      return;
    }
    const updatedOrder = {
      id: product.id,
      quantity: currentProductQuantity - 1
    };
    updateModularOrder({
      productDetails: updatedOrder,
      orientation: orientation?.currentSelection ?? {}
    });
    resetVariantGallery();
  };
  const addItemToOrder = async () => {
    const baseSku = product.sku + (product?.orientation?.option?.sku || '');
    logInfo('baseSKU: ', baseSku);
    if (product?.type?.name && !product?.type?.name?.toLowerCase().endsWith('slipcover')) {
      setHideMarxent(false);
      if (isMobile) {
        scrollToGallery();
      }
      if (!marxentInitialised) {
        setMarxentInitialised(true);
        const marxOptions = {
          apiKey: 'AIzaSyCYeL-al1cDsa6r9hcdpuLeZZzKxg8SdvE',
          rootElement: document.getElementById(`marxent-configurator-${slug}`),
          assetEnvironment: 'Stage',
          productConfigs: [{
            base: baseSku,
            configurations: new Map([['Cover', selectedOptionsVariantSku.split('-')[1]]])
          }]
        };
        try {
          const marx = new window.JOYSectional.JOYSectionalConfig();
          await marx.init(marxOptions);
          setMarxent(marx);
          logInfo('Marxent initialized for categoryId: ');
          const updatedOrder_0 = {
            id: product.id,
            quantity: currentProductQuantity + 1
          };
          updateModularOrder({
            productDetails: updatedOrder_0,
            orientation: orientation?.currentSelection ?? {}
          });
          resetVariantGallery();
        } catch (e) {
          setMarxentInitialised(false);
          logError('Marxent initialization error', e);
        }
        return;
      }
      if (marxentInitialised && marxent) {
        setHideMarxent(false);
        logInfo('Selected SKU: ', `${baseSku}|${selectedOptionsVariantSku.split('-')[1]}`);
        marxent.getEventManager().selectProduct({
          sku: `${baseSku}|${selectedOptionsVariantSku.split('-')[1]}`
        });
        resetVariantGallery();
        return;
      }
    }
    const updatedOrder_1 = {
      id: product.id,
      quantity: currentProductQuantity + 1
    };
    updateModularOrder({
      productDetails: updatedOrder_1,
      orientation: orientation?.currentSelection ?? {}
    });
    resetVariantGallery();
  };
  const onImageLoaded = (isFail = false) => {
    if (isFail) {
      setImageLoadFailed(true);
      return;
    }
    if (lastImage !== productImages.transparent_config_image && !imgLoadFailed) {
      setLastImage(productImages.transparent_config_image);
      setImageLoadFailed(false);
    }
  };
  useEffect(() => {
    if (isEmpty(selectedOptionsExtended) && !product) {
      return;
    }
    const productImgs = getImagesFromSelectedOptions({
      selectedOptionsExtended,
      product
    });
    if (!isEqual(productImgs, productImages)) {
      setProductImages(productImgs);
      setImageLoadFailed(false);
    }
  }, [selectedOptionsExtended, product, productImages]);
  const shouldFlip = orientation?.currentSelection?.value === 'Left';

  /* CON-1705 .Flip images for orientationDisplaySingleArmChaise */
  // if (
  //   orientation?.currentSelection?.sku === '-SCHSR' ||
  //   orientation?.currentSelection?.sku === '-SCHSL'
  // ) {
  //   shouldFlip = !shouldFlip;
  // }

  /* CON-6119 Single Arm Chair orientation flipped on modular parent PDP's
    Commenting the above logic to fix this issue.
    Leaving it here until we are sure this is not needed anymore.
   */

  return <div className="flex flex-row flex-nowrap px-0 py-6 justify-between items-start last:mb-4" key={`ModItem${product.id}`}>
      <div className="flex flex-row">
        <div className={cx('w-[90px] min-h-[74px] flex items-center justify-center mr-4 relative [&_img]:max-w-[70px] bg-gray-light8', {
        'border border-solid border-gray-light6': isCurrentModularSelected
      })}>
          {!isEmpty(productImages) ? <JBImage className={cx({
          'scale-x-[-1]': shouldFlip,
          '!opacity-30': imgLoadFailed
        })} src={imgLoadFailed ? lastImage : productImages.transparent_config_image} height={42} lazy={false} trim="color" onLoad={onImageLoaded} forPage="pdp" /> : null}
          {!imgLoadFailed ? <PDPMobileGalleryZoomIcon inverted={false} variant="ModularItem" onClick={() => {
          resetVariantGallery();
          onThumbnailClick();
        }} /> : <InfoTooltip modifier="brand-fade" openOnHover showCloseButton={isMobileOrTablet} variant="ModularItem" withArrow={false}>
              <span className="text-sm font-bold">
                Sorry, we don&apos;t have photos in this fabric for this
                product.
              </span>
            </InfoTooltip>}
        </div>

        <div className="flex flex-col items-start justify-start">
          <div className="text-base font-bold text-gray">
            {updatedProductName}
          </div>

          <div className="text-xs tracking-[.025rem] font-bold text-gray-light2 leading-[1rem] mb-2">{`${sizeWidth}"w x ${sizeDepth}"d x ${sizeHeight}"h`}</div>

          <div>
            {currentProductQuantity > 0 ? <Quantity minQuantity={min_quantity} onAddItem={addItemToOrder} onRemoveItem={removeItemToOrder} quantity={currentProductQuantity} unavailableFabric={unavailableFabric} variant="ModularItem" /> : isSelectionInProductOptions ? <button type="button" className={cx('text-sm font-bold flex items-center rounded-sm !border-0 justify-center px-4 py-2', {
            'bg-gray-light9 text-gray-light4 cursor-not-allowed': isDisabled,
            'bg-brand-lightest text-brand cursor-pointer': !isDisabled
          })} onClick={addItemToOrder} disabled={isDisabled}>
                <IconPlus color={isDisabled ? colorGrayLight4 : colorBrand} height={15} width={15} />
                &nbsp;
                <span className="ml-[3px]">Add</span>
              </button> : <span className="ModularItem__quantity-option__alert">
                {MISSING_OPTION_MSG}
              </span>}
          </div>
        </div>
      </div>

      <div className="text-base flex flex-row items-center justify-center font-bold">
        {discount_percent > 0 ? <div className="line-through text-gray">${original_price}</div> : null}

        <div data-xc="product-price" className={cx('ml-[.4rem]', {
        'text-gray': !discount_percent,
        'text-red': !!discount_percent
      })}>
          ${price}
        </div>
      </div>
    </div>;
};
ModularItem.propTypes = {
  product: PropTypes.object,
  selectedOptions: PropTypes.object,
  options: PropTypes.array,
  onThumbnailClick: PropTypes.func,
  updateModularOrder: PropTypes.func,
  updateGalleryProduct: PropTypes.func,
  isSelectionInProductOptions: PropTypes.bool,
  selectedOptionsExtended: PropTypes.object,
  modularState: PropTypes.object,
  selectedOptionsVariantSku: PropTypes.string,
  setHideMarxent: PropTypes.func,
  unavailableFabric: PropTypes.bool,
  marxentInitialised: PropTypes.bool,
  setMarxentInitialised: PropTypes.func,
  slug: PropTypes.string,
  setMarxent: PropTypes.func,
  marxent: PropTypes.object,
  productsIn3dLoader: PropTypes.object,
  scrollToGallery: PropTypes.func
};
export default ModularItem;